import { createSlice, createAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    WritingPrompt,
    WritingRecommendation,
    WritingPromptFilters,
    PaginatedResponse,
    UserWritingProfile,
    WritingType,
    DifficultyLevel,
    WritingAttempt,
    WritingSession,
    WritingFeedbackResponse,
} from '@/services/writing/types';
import { START_PAGE } from '@/common/constants';

// === WRITING ACTIONS ===
export const fetchWritingPromptsRequest = createAction<WritingPromptFilters | undefined>('writing/fetchPromptsRequest');
export const fetchWritingPromptsSuccess = createAction<PaginatedResponse<WritingPrompt>>('writing/fetchPromptsSuccess');
export const fetchWritingPromptsFailure = createAction<string>('writing/fetchPromptsFailure');

export const fetchWritingPromptByIdRequest = createAction<string>('writing/fetchPromptByIdRequest');
export const fetchWritingPromptByIdSuccess = createAction<WritingPrompt>('writing/fetchPromptByIdSuccess');
export const fetchWritingPromptByIdFailure = createAction<string>('writing/fetchPromptByIdFailure');

export const searchWritingPromptsRequest = createAction<{ query: string; filters?: WritingPromptFilters }>(
    'writing/searchPromptsRequest',
);
export const searchWritingPromptsSuccess = createAction<WritingPrompt[]>('writing/searchPromptsSuccess');
export const searchWritingPromptsFailure = createAction<string>('writing/searchPromptsFailure');

// === CREATE WRITING SESSION ===
export const createWritingSessionRequest = createAction<{
    prompt_id: string;
    session_type: string;
    difficulty_level: string;
}>('writing/createSessionRequest');
export const createWritingSessionSuccess = createAction<WritingSession>('writing/createSessionSuccess');
export const createWritingSessionFailure = createAction<string>('writing/createSessionFailure');

// === FETCH WRITING SESSION BY ID ===
export const fetchWritingSessionByIdRequest = createAction<string>('writing/fetchSessionByIdRequest');
export const fetchWritingSessionByIdSuccess = createAction<WritingSession>('writing/fetchSessionByIdSuccess');
export const fetchWritingSessionByIdFailure = createAction<string>('writing/fetchSessionByIdFailure');

// === SUBMIT WRITING ANSWER ===
export const submitWritingAnswerRequest = createAction<{
    prompt_id: string;
    content: string;
    word_count: number;
    time_spent_minutes: number;
}>('writing/submitAnswerRequest');
export const submitWritingAnswerSuccess = createAction<WritingAttempt>('writing/submitAnswerSuccess');
export const submitWritingAnswerFailure = createAction<string>('writing/submitAnswerFailure');

// === SUBMIT WRITING ===
export const submitWritingRequest = createAction<{
    prompt_id: string;
    content: string;
    word_count: number;
    time_spent_minutes: number;
    session_id: string;
}>('writing/submitWritingRequest');
export const submitWritingSuccess = createAction<WritingFeedbackResponse>('writing/submitWritingSuccess');
export const submitWritingFailure = createAction<string>('writing/submitWritingFailure');

export const fetchWritingRecommendationsRequest = createAction<number | undefined>(
    'writing/fetchRecommendationsRequest',
);
export const fetchWritingRecommendationsSuccess = createAction<WritingRecommendation[]>(
    'writing/fetchRecommendationsSuccess',
);
export const fetchWritingRecommendationsFailure = createAction<string>('writing/fetchRecommendationsFailure');

export const rateWritingRecommendationRequest = createAction<{ recommendationId: string; rating: number }>(
    'writing/rateRecommendationRequest',
);
export const rateWritingRecommendationSuccess = createAction('writing/rateRecommendationSuccess');
export const rateWritingRecommendationFailure = createAction<string>('writing/rateRecommendationFailure');

export const fetchUserWritingProfileRequest = createAction('writing/fetchUserProfileRequest');
export const fetchUserWritingProfileSuccess = createAction<UserWritingProfile>('writing/fetchUserProfileSuccess');
export const fetchUserWritingProfileFailure = createAction<string>('writing/fetchUserProfileFailure');

export const updateUserWritingProfileRequest = createAction<{
    preferred_writing_types?: WritingType[];
    writing_goals?: any;
}>('writing/updateUserProfileRequest');
export const updateUserWritingProfileSuccess = createAction<UserWritingProfile>('writing/updateUserProfileSuccess');
export const updateUserWritingProfileFailure = createAction<string>('writing/updateUserProfileFailure');

// === FILTERS & UI ACTIONS ===
export const setWritingPromptFilters = createAction<WritingPromptFilters>('writing/setFilters');
export const clearWritingPromptFilters = createAction('writing/clearFilters');
export const setCurrentWritingType = createAction<WritingType>('writing/setCurrentWritingType');
export const setCurrentDifficultyLevel = createAction<DifficultyLevel>('writing/setCurrentDifficultyLevel');

// === STATE INTERFACE ===
export interface WritingState {
    // Prompts
    prompts: WritingPrompt[];
    isLoadingPrompts: boolean;
    promptsError: string | null;
    totalPrompts: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;

    // Current prompt
    currentPrompt: WritingPrompt | null;
    isLoadingPrompt: boolean;
    promptError: string | null;

    // Create session
    isCreatingSession: boolean;
    createSessionError: string | null;
    currentSession: WritingSession | null;

    // Fetch session by ID
    isLoadingSession: boolean;
    sessionError: string | null;
    fetchedSession: WritingSession | null;

    // Submit answer
    isSubmittingAnswer: boolean;
    submitAnswerError: string | null;
    lastSubmittedAnswer: WritingAttempt | null;

    // Submit writing feedback
    lastSubmittedFeedback: WritingFeedbackResponse | null;

    // Search
    searchResults: WritingPrompt[];
    isSearching: boolean;
    searchError: string | null;
    lastSearchQuery: string;

    // Recommendations
    recommendations: WritingRecommendation[];
    isLoadingRecommendations: boolean;
    recommendationsError: string | null;
    isRatingRecommendation: boolean;
    ratingError: string | null;

    // User profile
    userProfile: UserWritingProfile | null;
    isLoadingProfile: boolean;
    profileError: string | null;
    isUpdatingProfile: boolean;
    updateProfileError: string | null;

    // Filters and UI state
    filters: WritingPromptFilters;
    currentWritingType: WritingType | null;
    currentDifficultyLevel: DifficultyLevel | null;
}

// === INITIAL STATE ===
const initialState: WritingState = {
    // Prompts
    prompts: [],
    isLoadingPrompts: false,
    promptsError: null,
    totalPrompts: 0,
    currentPage: START_PAGE,
    hasNextPage: false,
    hasPreviousPage: false,

    // Current prompt
    currentPrompt: null,
    isLoadingPrompt: false,
    promptError: null,

    // Create session
    isCreatingSession: false,
    createSessionError: null,
    currentSession: null,

    // Fetch session by ID
    isLoadingSession: false,
    sessionError: null,
    fetchedSession: null,

    // Submit answer
    isSubmittingAnswer: false,
    submitAnswerError: null,
    lastSubmittedAnswer: null,

    // Submit writing feedback
    lastSubmittedFeedback: null,

    // Search
    searchResults: [],
    isSearching: false,
    searchError: null,
    lastSearchQuery: '',

    // Recommendations
    recommendations: [],
    isLoadingRecommendations: false,
    recommendationsError: null,
    isRatingRecommendation: false,
    ratingError: null,

    // User profile
    userProfile: null,
    isLoadingProfile: false,
    profileError: null,
    isUpdatingProfile: false,
    updateProfileError: null,

    // Filters and UI state
    filters: {},
    currentWritingType: null,
    currentDifficultyLevel: null,
};

// === SLICE DEFINITION ===
const writingSlice = createSlice({
    name: 'writing',
    initialState,
    reducers: {
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === FETCH PROMPTS ===
            .addCase(fetchWritingPromptsRequest, (state, action) => {
                state.isLoadingPrompts = true;
                state.promptsError = null;
                if (action.payload) {
                    state.filters = action.payload;
                }
            })
            .addCase(fetchWritingPromptsSuccess, (state, action) => {
                state.isLoadingPrompts = false;
                state.prompts = action.payload.results;
                state.totalPrompts = action.payload.count;
                state.hasNextPage = !!action.payload.next;
                state.hasPreviousPage = !!action.payload.previous;
            })
            .addCase(fetchWritingPromptsFailure, (state, action) => {
                state.isLoadingPrompts = false;
                state.promptsError = action.payload;
            })

            // === SUBMIT ANSWER ===
            .addCase(submitWritingAnswerRequest, (state) => {
                state.isSubmittingAnswer = true;
                state.submitAnswerError = null;
            })
            .addCase(submitWritingAnswerSuccess, (state, action) => {
                state.isSubmittingAnswer = false;
                state.lastSubmittedAnswer = action.payload;
            })
            .addCase(submitWritingAnswerFailure, (state, action) => {
                state.isSubmittingAnswer = false;
                state.submitAnswerError = action.payload;
            })

            // === SUBMIT WRITING ===
            .addCase(submitWritingRequest, (state) => {
                state.isSubmittingAnswer = true;
                state.submitAnswerError = null;
            })
            .addCase(submitWritingSuccess, (state, action) => {
                state.isSubmittingAnswer = false;
                state.lastSubmittedFeedback = action.payload;
            })
            .addCase(submitWritingFailure, (state, action) => {
                state.isSubmittingAnswer = false;
                state.submitAnswerError = action.payload;
            })

            // === FETCH PROMPT BY ID ===
            .addCase(fetchWritingPromptByIdRequest, (state) => {
                state.isLoadingPrompt = true;
                state.promptError = null;
            })
            .addCase(fetchWritingPromptByIdSuccess, (state, action) => {
                state.isLoadingPrompt = false;
                state.currentPrompt = action.payload;
            })
            .addCase(fetchWritingPromptByIdFailure, (state, action) => {
                state.isLoadingPrompt = false;
                state.promptError = action.payload;
            })

            // === CREATE SESSION ===
            .addCase(createWritingSessionRequest, (state) => {
                state.isCreatingSession = true;
                state.createSessionError = null;
            })
            .addCase(createWritingSessionSuccess, (state, action) => {
                state.isCreatingSession = false;
                state.currentSession = action.payload;
            })
            .addCase(createWritingSessionFailure, (state, action) => {
                state.isCreatingSession = false;
                state.createSessionError = action.payload;
            })

            // === FETCH WRITING SESSION BY ID ===
            .addCase(fetchWritingSessionByIdRequest, (state) => {
                state.isLoadingSession = true;
                state.sessionError = null;
            })
            .addCase(fetchWritingSessionByIdSuccess, (state, action) => {
                state.isLoadingSession = false;
                state.fetchedSession = action.payload;
            })
            .addCase(fetchWritingSessionByIdFailure, (state, action) => {
                state.isLoadingSession = false;
                state.sessionError = action.payload;
            })

            // === SEARCH PROMPTS ===
            .addCase(searchWritingPromptsRequest, (state, action) => {
                state.isSearching = true;
                state.searchError = null;
                state.lastSearchQuery = action.payload.query;
            })
            .addCase(searchWritingPromptsSuccess, (state, action) => {
                state.isSearching = false;
                state.searchResults = action.payload;
            })
            .addCase(searchWritingPromptsFailure, (state, action) => {
                state.isSearching = false;
                state.searchError = action.payload;
            })

            // === FETCH RECOMMENDATIONS ===
            .addCase(fetchWritingRecommendationsRequest, (state) => {
                state.isLoadingRecommendations = true;
                state.recommendationsError = null;
            })
            .addCase(fetchWritingRecommendationsSuccess, (state, action) => {
                state.isLoadingRecommendations = false;
                state.recommendations = action.payload;
            })
            .addCase(fetchWritingRecommendationsFailure, (state, action) => {
                state.isLoadingRecommendations = false;
                state.recommendationsError = action.payload;
            })

            // === RATE RECOMMENDATION ===
            .addCase(rateWritingRecommendationRequest, (state) => {
                state.isRatingRecommendation = true;
                state.ratingError = null;
            })
            .addCase(rateWritingRecommendationSuccess, (state) => {
                state.isRatingRecommendation = false;
            })
            .addCase(rateWritingRecommendationFailure, (state, action) => {
                state.isRatingRecommendation = false;
                state.ratingError = action.payload;
            })

            // === FETCH USER PROFILE ===
            .addCase(fetchUserWritingProfileRequest, (state) => {
                state.isLoadingProfile = true;
                state.profileError = null;
            })
            .addCase(fetchUserWritingProfileSuccess, (state, action) => {
                state.isLoadingProfile = false;
                state.userProfile = action.payload;
            })
            .addCase(fetchUserWritingProfileFailure, (state, action) => {
                state.isLoadingProfile = false;
                state.profileError = action.payload;
            })

            // === UPDATE USER PROFILE ===
            .addCase(updateUserWritingProfileRequest, (state) => {
                state.isUpdatingProfile = true;
                state.updateProfileError = null;
            })
            .addCase(updateUserWritingProfileSuccess, (state, action) => {
                state.isUpdatingProfile = false;
                state.userProfile = action.payload;
            })
            .addCase(updateUserWritingProfileFailure, (state, action) => {
                state.isUpdatingProfile = false;
                state.updateProfileError = action.payload;
            })

            // === FILTERS & UI ===
            .addCase(setWritingPromptFilters, (state, action) => {
                state.filters = action.payload;
            })
            .addCase(clearWritingPromptFilters, (state) => {
                state.filters = {};
                state.currentPage = 1;
            })
            .addCase(setCurrentWritingType, (state, action) => {
                state.currentWritingType = action.payload;
            })
            .addCase(setCurrentDifficultyLevel, (state, action) => {
                state.currentDifficultyLevel = action.payload;
            });
    },
});

export const { setCurrentPage, setFilters } = writingSlice.actions;

export default writingSlice.reducer;
